import React, { useState } from "react";
import {
  FaRocket,
  FaExpandAlt,
  FaUsers,
  FaGlobe,
  FaUserCircle,
  FaImage,
  FaMoneyBillWave,
  FaTools,
  FaChartLine,
  FaMobileAlt,
  FaVoteYea,
  FaInfoCircle,
  FaLightbulb,
} from "react-icons/fa";
import { SiCreatereactapp } from "react-icons/si";
import { GiBurningEmbers, GiStumpRegrowth, GiTreeGrowth } from "react-icons/gi";
import { GrUpdate } from "react-icons/gr";
import { motion } from "framer-motion";

type RoadmapItem = {
  version: string;
  title: string;
  icon: JSX.Element;
  features: string[];
  description?: string;
};

type RoadmapData = {
  iBird: RoadmapItem[];
  iAssets: RoadmapItem[];
};

const Roadmap: React.FC = () => {
  const [activeTab, setActiveTab] = useState<"iBird" | "iAssets">("iBird");
  const [hoveredCard, setHoveredCard] = useState<number | null>(null);

  const roadmapData: RoadmapData = {
    iBird: [
      {
        version: "v0.0.1",
        title: "Threads",
        icon: <FaRocket />,
        description:
          "Basic messaging system - Think of it like creating chat rooms where people can talk",
        features: [
          "🟢 Connect wallet - Link your digital wallet to start using the app",
          "🟢 Create threads - Start new conversation topics",
          "🟢 Read thread's data - View all messages in a conversation",
          "🟢 Send message to threads - Post your thoughts and replies",
        ],
      },
      {
        version: "v0.0.2",
        title: "Interactions",
        icon: <FaUsers />,
        description:
          "Social features - Like Facebook reactions but on blockchain",
        features: [
          "🟢 Reply to messages - Respond directly to specific posts",
          "🟢 Like - Show appreciation for posts you enjoy",
          "🟢 Dislike - Express disagreement with content",
          "🟢 Write Comments - Add detailed responses to discussions",
        ],
      },
      {
        version: "v0.0.3",
        title: "Explore Feed",
        icon: <FaGlobe />,
        description:
          "Discovery system - Like Twitter's trending page but decentralized",
        features: [
          "🟢 Directory for all threads - Browse all public conversations",
          "🟢 Explore Topic: 0.0.3946144 - Special discovery channel on Hedera",
          "🟢 Users can send their threads to the Explore Feed - Share your conversations publicly",
        ],
      },
      {
        version: "v0.0.4",
        title: "Profile & Planet",
        icon: <FaUserCircle />,
        description:
          "User identity system - Your digital ID card and personal space on the blockchain",
        features: [
          "🟢 Create user Profile NFT - Your unique digital identity token",
          "🟢 Create user Profile Topic - Your personal data storage on Hedera",
          "🟢 Save Profile Topic in Profile NFT - Link your data to your identity",
          "🟢 Create user Threads - Personal conversation spaces",
          "🟢 Save user Threads in Profile Topic - Keep track of your conversations",
          "🟢 Planet Topic: 0.0.4320596 - Global community hub on Hedera network",
        ],
      },
      {
        version: "v0.0.5",
        title: "Media Support",
        icon: <FaImage />,
        description:
          "Rich content sharing - Upload photos and videos like Instagram",
        features: [
          "🟢 Save media files on IPFS - Store images/videos on decentralized storage",
          "🟢 Send media files with messages - Share photos and videos in conversations",
        ],
      },
      {
        version: "v0.0.6",
        title: "Tipping Support",
        icon: <FaMoneyBillWave />,
        description:
          "Digital payments - Send crypto tips to creators you support",
        features: [
          "🟢 Tip HBAR, ASSET - Send native Hedera tokens and our project token",
          "🟢 Tip SAUCE, GRELF, USDC - Support with popular community tokens",
          "🟢 Tip DOVU, SAUCEINU, JAM - More token options for diverse community",
          "🟢 Tip HSUITE, BSL, BULLBAR - Additional ecosystem tokens for tipping",
        ],
      },
      {
        version: "v0.0.7",
        title: "UPGRADO",
        icon: <FaTools />,
        description:
          "Enhanced user experience - Major improvements to make the app easier to use",
        features: [
          "🟢 Hash Link # - Easy way to reference and link to specific content",
          "🟢 Dark & light theme - Choose your preferred visual style",
          "🟢 Universal Topic - Global communication channel for all users",
          "🟢 Interactive polls - Create and vote on community questions",
          "🟢 Users profile - Enhanced personal pages with more information",
        ],
      },
      {
        version: "v0.0.8",
        title: "Reward & Analyze",
        icon: <FaChartLine />,
        description:
          "Smart incentives - Earn rewards for good participation and track platform growth",
        features: [
          "🟢 Reward engine - Automatic system to reward active and helpful users",
          "🟢 System Analytics - Track platform usage and community growth metrics",
        ],
      },
      {
        version: "v0.0.9",
        title: "Applications",
        icon: <FaMobileAlt />,
        description:
          "Mobile apps and polish - Professional mobile apps for iOS and Android",
        features: [
          "🟢 Date & Time Stamps - See exactly when messages were posted",
          "🟢 Character Counter - Know how much text you can still type",
          "🟢 Link, #, $ Reader - Smart detection of links, hashtags, and prices",
          "🟢 Explorer Infinite Scrolling - Smooth browsing without page breaks",
          "🟢 Wallet Connect - Improved wallet connection experience",
          "🟢 IOS Application - Native iPhone and iPad app",
          "🟢 Android Application - Native Android phone and tablet app",
          "🟢 New Logo - Fresh visual identity for the platform",
        ],
      },
      {
        version: "v0.1.0",
        title: "Community Governance",
        icon: <FaVoteYea />,
        description:
          "Democratic control - Community members vote on platform decisions and improvements",
        features: [
          "🟠 List Android Application on Android Stores - Make app available on Google Play",
          "🟠 List IOS Application on IOS Stores - Make app available on Apple App Store",
          "🟢 Repost - Share others' content with your followers",
          "🟠 Followers - Build your audience and follow interesting users",
          "🟠 Channels - Create specialized topic-based communities",
          "🟠 Private Messages - Send direct messages to other users",
          "🟠 New Profile System - Enhanced user profiles with more customization",
          "🟠 New Structure - Improved app organization for better user experience",
          "🟠 Special Features for iBird Gen 1 NFT Holders - Exclusive benefits for early supporters",
          "🟠 iBird Gen 1: The First Flight - NFT holders propose and vote on key platform updates",
        ],
      },
    ],
    iAssets: [
      {
        version: "Stage 1",
        title: "Creation",
        icon: <SiCreatereactapp />,
        description:
          "The beginning - Started as NFTBlackMarket, a place to trade digital collectibles",
        features: [
          "🟢 Creation of NFTBlackMarket - Built the first version of our platform",
          "🟢 NFTBlackMarket Website Launch - Made the platform available to users",
          "🟢 Creation of NBM (BSC) Token - Launched our first cryptocurrency token",
          "🟢 NBM Token Listed on PancakeSwap - Made token tradeable on major exchange",
          "🟢 NBM Liquidity Locked Forever - Secured trading stability permanently",
          "🟢 NBM Smartcontract Audited by TechRate - Professional security verification",
          "🟢 Launch of NFT Marketplace - Platform for buying and selling digital art",
        ],
      },
      {
        version: "Stage 2",
        title: "Rebranding",
        icon: <GrUpdate />,
        description:
          "New identity - Changed name to iAssets to better reflect our vision",
        features: [
          "🟢 NFTBlackMarket Rebranded to iAssets - New name, same great service",
          "🟢 NBM (BSC)Token Rebranded to ASSET (BSC) - Updated token name to match brand",
          "🟢 NFT Marketplace Rebranded as iAssets.market - New domain and branding",
          "🟢 iAssets.org Website Launch - New main website with improved design",
          "🟢 NBM (BSC) to ASSET (BSC) Converter - Tool to upgrade old tokens to new ones",
        ],
      },
      {
        version: "Stage 3",
        title: "Expansion",
        icon: <FaExpandAlt />,
        description:
          "Growing reach - Added more features and got listed on exchanges",
        features: [
          "🟢 ASSET Token Listed on IndoEx - Made token available on another exchange",
          "🟢 Launch of iAssets.Social (Social Media) on BSC - Social platform on Binance network",
          "🟢 Launch of iAssets.Marketing - Tools and services for crypto marketing",
        ],
      },
      {
        version: "Stage 4",
        title: "Destruction",
        icon: <GiBurningEmbers />,
        description:
          "Strategic reset - Closed old services to focus on better technology",
        features: [
          "🟢 iAssets.Market (NFT Platform) deprecated - Closed old NFT marketplace",
          "🟢 iAssets.Marketing deprecated - Ended marketing services",
          "🟢 NBM (BSC) Token deprecated - Phased out original token",
          "🟢 ASSET (BSC) Token deprecated - Ended Binance Smart Chain version",
          "🟢 iAssets.Social on BSC deprecated - Closed social platform on old network",
        ],
      },
      {
        version: "Stage 5",
        title: "Growth",
        icon: <GiStumpRegrowth />,
        description:
          "New foundation - Moved to Hedera network for better performance and sustainability",
        features: [
          "🟢 Migration to Hedera - Switched to faster, greener blockchain technology",
          "🟢 Launch iAssets.Social on Hedera - Rebuilt social platform on new network",
          "🟢 Creation of ASSET(HTS) Token on Hedera - New token on superior blockchain",
          "🟢 NBM (BSC), ASSET(BSC) to ASSET(HTS) Converter - Bridge from old to new tokens",
          "🟢 Rebrand iAssets.Social to iBird.io - New name for our social platform",
          "🟢 The project became Open-Source & Community-Driven - Made code public and community-owned",
          "🟢 Launch of iBird.community - Community hub for users and developers",
          "🟢 Redesign iAssets.org - Fresh new website design and user experience",
          "🟢 Transfer iBird.community website to iBird.io - Unified under one domain",
        ],
      },
      {
        version: "Stage 6",
        title: "Community",
        icon: <GiTreeGrowth />,
        description:
          "Massive adoption - Growing our user base from hundreds to billions of users worldwide",
        features: [
          "🟠 100 Users - First milestone of active community members",
          "🟠 1,000 Users - Growing community with regular engagement",
          "🟠 10,000 Users - Significant user base with diverse content",
          "🟠 100,000 Users - Large community with viral content potential",
          "🟠 1,000,000 Users - Major social platform with mainstream recognition",
          "🟠 10,000,000 Users - Global platform competing with major social networks",
          "🟠 100,000,000 Users - Massive user base with worldwide influence",
          "🟠 1,000,000,000 Users - Billion-user platform reshaping social media",
        ],
      },
    ],
  };

  return (
    <div className="relative w-full py-16 sm:py-24 lg:py-32 overflow-hidden bg-gradient-to-b from-gray-50 to-white">
      {/* Enhanced background elements */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.08 }}
        transition={{ duration: 2 }}
        className="absolute inset-0"
        style={{
          backgroundImage:
            "radial-gradient(circle at 50% 50%, #FF6B6B 1.5px, transparent 1.5px)",
          backgroundSize: "60px 60px",
        }}
      />

      {/* Enhanced floating elements with more subtle animations */}
      <motion.div
        animate={{
          y: [0, -20, 0],
          scale: [1, 1.1, 1],
          opacity: [0.6, 0.8, 0.6],
        }}
        transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
        className="absolute left-4 sm:left-10 top-16 sm:top-32 w-20 sm:w-40 h-20 sm:h-40 bg-gradient-to-r from-orange-400/20 to-yellow-400/20 rounded-full blur-2xl"
      />
      <motion.div
        animate={{ y: [0, 20, 0] }}
        transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
        className="absolute right-4 sm:right-10 bottom-16 sm:bottom-32 w-16 sm:w-32 h-16 sm:h-32 bg-gradient-to-r from-teal-400/30 to-blue-400/30 rounded-full blur-xl"
      />

      <div className="relative container mx-auto px-4 sm:px-6">
        {/* Project Evolution Explanation */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: "easeOut" }}
          viewport={{ once: true }}
          className="text-center mb-12 sm:mb-16"
        >
          <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-6 sm:p-8 shadow-xl max-w-4xl mx-auto">
            <div className="flex items-center justify-center mb-4">
              <FaInfoCircle className="text-3xl sm:text-4xl text-blue-500 mr-3" />
              <h3 className="text-2xl sm:text-3xl font-bold text-gray-800">
                Our Evolution Story
              </h3>
            </div>
            <div className="text-left space-y-4 text-base sm:text-lg text-gray-700 leading-relaxed">
              <div className="relative bg-gray-50 p-4 pl-6 rounded-xl shadow-sm">
                <span className="absolute left-2 top-5 w-1 h-5 bg-gradient-to-b from-blue-400 to-purple-400 rounded-full"></span>
                <p className="text-base sm:text-lg text-gray-700 leading-relaxed">
                  <strong>What we were before:</strong> We started as
                  NFTBlackMarket, a simple NFT trading platform on Binance Smart
                  Chain. We had basic features but faced limitations with high
                  fees and environmental concerns.
                </p>
              </div>
              <div className="relative bg-gray-50 p-4 pl-6 rounded-xl shadow-sm">
                <span className="absolute left-2 top-5 w-1 h-5 bg-gradient-to-b from-green-400 to-blue-400 rounded-full"></span>
                <p className="text-base sm:text-lg text-gray-700 leading-relaxed">
                  <strong>What we are now:</strong> We've evolved into iBird - a
                  comprehensive social platform on Hedera network. We're now
                  faster, greener, and more community-focused than ever before.
                </p>
              </div>
              <div className="relative bg-gradient-to-r from-purple-100 to-pink-100 p-4 pl-6 rounded-xl shadow-md">
                <span className="absolute left-2 top-5 w-1 h-8 bg-gradient-to-b from-yellow-400 to-orange-400 rounded-full"></span>
                <p className="text-base sm:text-lg text-gray-700 leading-relaxed">
                  <FaLightbulb className="inline text-yellow-500 mr-2" />
                  <strong>
                    We are adapting with changes and continue to grow
                  </strong>{" "}
                  by listening to our community, embracing new technology, and
                  building features that truly matter to our users. Every update
                  brings us closer to creating the ultimate decentralized social
                  experience.
                </p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Enhanced title animation */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: "easeOut" }}
          viewport={{ once: true }}
          className="text-center mb-10 sm:mb-14"
        >
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-purple-600 via-pink-500 to-orange-500 mb-4 sm:mb-6 hover:scale-105 transition-transform duration-300">
            Our Journey
          </h2>
          <p className="text-lg sm:text-xl text-gray-600 max-w-2xl mx-auto">
            From simple beginnings to revolutionary social platform - see how
            we've grown and where we're heading
          </p>
        </motion.div>

        {/* Enhanced tab buttons */}
        <div className="flex justify-center mb-8 sm:mb-12">
          {["iBird", "iAssets"].map((tab) => (
            <motion.button
              key={tab}
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setActiveTab(tab as "iBird" | "iAssets")}
              className={`px-6 sm:px-8 py-3 mx-2 text-lg sm:text-xl font-semibold rounded-full transition-all duration-300 ${
                activeTab === tab
                  ? "bg-gradient-to-r from-purple-600 via-pink-500 to-orange-500 text-white shadow-lg"
                  : "text-gray-600 hover:bg-gray-100 bg-white/50"
              }`}
            >
              {tab === "iBird" ? "🐦 iBird Platform" : "🏢 iAssets History"}
            </motion.button>
          ))}
        </div>

        {/* Tab descriptions */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-8 sm:mb-12"
        >
          <div className="bg-white/60 backdrop-blur-sm rounded-2xl p-4 sm:p-6 max-w-3xl mx-auto">
            <p className="text-base sm:text-lg text-gray-700">
              {activeTab === "iBird"
                ? "🚀 Follow iBird's development from basic messaging to a full social platform with governance features"
                : "📈 See how iAssets transformed from a simple NFT marketplace to a community-driven ecosystem"}
            </p>
          </div>
        </motion.div>

        {/* Enhanced grid layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 max-w-7xl mx-auto">
          {roadmapData[activeTab].map((item, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
              onHoverStart={() => setHoveredCard(index)}
              onHoverEnd={() => setHoveredCard(null)}
              className={`bg-white/80 backdrop-blur-sm p-6 sm:p-8 rounded-3xl shadow-xl transition-all duration-500
                ${
                  hoveredCard === index
                    ? "scale-[1.02] shadow-2xl bg-white/90"
                    : "hover:shadow-xl"
                }`}
            >
              <div className="flex items-start mb-6">
                <motion.div
                  whileHover={{ rotate: 360 }}
                  transition={{ duration: 0.5 }}
                  className="p-3 sm:p-4 bg-gradient-to-r from-[#FF6B6B] via-[#FF8E53] to-[#FFA33C] rounded-2xl text-white text-2xl sm:text-4xl shadow-lg flex-shrink-0"
                >
                  {item.icon}
                </motion.div>
                <div className="ml-4 flex-1 min-w-0">
                  <span className="text-base sm:text-lg font-semibold text-[#FF6B6B] block">
                    {item.version}
                  </span>
                  <h3 className="text-xl sm:text-2xl font-bold text-gray-800 mb-2">
                    {item.title}
                  </h3>
                  {item.description && (
                    <div className="relative bg-gray-50 p-3 pl-6 rounded-xl shadow-sm italic">
                      <span className="absolute left-2 top-1/2 -translate-y-1/2 w-1 h-10 bg-gradient-to-b from-blue-400 to-purple-400 rounded-full"></span>
                      <p className="text-sm sm:text-base text-gray-600">
                        💡 {item.description}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              <ul className="space-y-3">
                {item.features.map((feature, i) => (
                  <motion.li
                    key={i}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3, delay: i * 0.05 }}
                    viewport={{ once: true }}
                    whileHover={{ x: 5 }}
                    className="flex items-start text-gray-700 hover:text-gray-900 group"
                  >
                    <span
                      className={`w-3 h-3 rounded-full mr-3 flex-shrink-0 mt-2 transition-all duration-300 group-hover:scale-125 ${
                        feature.startsWith("🟢")
                          ? "bg-green-500 shadow-green-200 shadow-lg"
                          : "bg-yellow-500 shadow-yellow-200 shadow-lg animate-pulse"
                      }`}
                    />
                    <span className="text-sm sm:text-base leading-relaxed">
                      {feature.slice(2)}
                    </span>
                  </motion.li>
                ))}
              </ul>

              {/* Progress indicator */}
              <div className="mt-6 pt-4 border-t border-gray-200">
                <div className="flex items-center justify-between text-sm text-gray-500">
                  <span>Progress:</span>
                  <span className="font-semibold">
                    {item.features.filter((f) => f.startsWith("🟢")).length} /{" "}
                    {item.features.length} completed
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                  <div
                    className="bg-gradient-to-r from-green-400 to-green-600 h-2 rounded-full transition-all duration-1000"
                    style={{
                      width: `${
                        (item.features.filter((f) => f.startsWith("🟢"))
                          .length /
                          item.features.length) *
                        100
                      }%`,
                    }}
                  />
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Legend */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          viewport={{ once: true }}
          className="mt-12 sm:mt-16 text-center"
        >
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 sm:p-8 max-w-2xl mx-auto shadow-lg">
            <h4 className="text-lg sm:text-xl font-bold text-gray-800 mb-4">
              Legend
            </h4>
            <div className="flex flex-col sm:flex-row justify-center items-center space-y-4 sm:space-y-0 sm:space-x-8">
              <div className="flex items-center">
                <span className="w-4 h-4 bg-green-500 rounded-full mr-3 shadow-lg"></span>
                <span className="text-sm sm:text-base text-gray-700">
                  🟢 Completed - Already working!
                </span>
              </div>
              <div className="flex items-center">
                <span className="w-4 h-4 bg-yellow-500 rounded-full mr-3 shadow-lg animate-pulse"></span>
                <span className="text-sm sm:text-base text-gray-700">
                  🟠 In Progress - Coming soon!
                </span>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Roadmap;
